import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { AuthShell, GoogleButton } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, GraduationCap, Car } from "lucide-react";

export default function Register() {
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [role, setRole] = useState("student");
  const [inviteCode, setInviteCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const payload = { ...form, role };
      if (role === "instructor") payload.invitation_code = inviteCode;
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    { key: "student", label: "Student", desc: "Book lessons", icon: GraduationCap },
    { key: "instructor", label: "Instructor", desc: "Teach & manage", icon: Car },
  ];

  return (
    <AuthShell title="Create your account" subtitle="Start booking or teaching driving lessons.">
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        {error && <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-lg px-3 py-2" data-testid="register-error">{error}</div>}

        <div className="grid grid-cols-2 gap-3">
          {roles.map((r) => (
            <button type="button" key={r.key} onClick={() => setRole(r.key)} data-testid={`role-${r.key}`}
              className={`rounded-xl border p-4 text-left transition-colors duration-200 ${role === r.key ? "border-arctic-primary bg-arctic-primary/5 ring-2 ring-arctic-primary/30" : "border-border hover:border-arctic-primary/50"}`}>
              <r.icon className={`w-5 h-5 mb-2 ${role === r.key ? "text-arctic-primary" : "text-muted-foreground"}`} />
              <div className="font-heading font-semibold text-sm text-arctic-secondary">{r.label}</div>
              <div className="text-xs text-muted-foreground">{r.desc}</div>
            </button>
          ))}
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="register-name" placeholder="Jane Frost" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required data-testid="register-email" placeholder="you@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="register-password" placeholder="At least 6 characters" />
        </div>
        {role === "instructor" && (
          <div className="space-y-2">
            <Label htmlFor="invite">Instructor invitation code</Label>
            <Input id="invite" value={inviteCode} onChange={(e) => setInviteCode(e.target.value)} required data-testid="register-invite-code" placeholder="ARCTIC-XXXX-XXXX" className="font-mono uppercase" />
            <p className="text-xs text-muted-foreground">Instructor accounts require an invitation from an administrator.</p>
          </div>
        )}
        <Button type="submit" disabled={loading} data-testid="register-submit" className="w-full h-11 rounded-full bg-arctic-primary hover:bg-arctic-primary/90">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>

      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-border" /><span className="text-xs text-muted-foreground">OR</span><div className="flex-1 h-px bg-border" />
      </div>
      <GoogleButton label="Sign up with Google" />

      <p className="text-sm text-muted-foreground mt-6 text-center">
        Already have an account? <Link to="/login" className="text-arctic-primary font-semibold hover:underline" data-testid="to-login">Log in</Link>
      </p>
    </AuthShell>
  );
}
